import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddFranchise from "../../Masters/Scope3/Franchise/AddFranchise";

const EXCEL_FRANCHISE_ID_KEY = "Franchise ID";

const getEnteredFranchiseId = (row) =>
  row?.franchiseId ||
  row?.franchiseID ||
  row?.["Franchise ID"] ||
  row?.["FranchiseID"] ||
  row?.["franchiseId"] ||
  row?.["franchiseID"] ||
  "";

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.franchiseMatched === "boolean") return row.franchiseMatched;
  if (typeof row.franchiseIdMatched === "boolean") return row.franchiseIdMatched;
  if (typeof row.franchiseIDMatched === "boolean") return row.franchiseIDMatched;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.franchiseMatched === "boolean")
    return { ...row, franchiseMatched: next };
  if (typeof row.franchiseIdMatched === "boolean")
    return { ...row, franchiseIdMatched: next };
  if (typeof row.franchiseIDMatched === "boolean")
    return { ...row, franchiseIDMatched: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, matched: next };
};

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl,
}) => {
  const [masterFranchises, setMasterFranchises] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [addMasterOpen, setAddMasterOpen] = useState(false);
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [addMasterElement, setAddMasterElement] = useState(null);
  const [pendingCreatedFranchiseId, setPendingCreatedFranchiseId] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const masterFormInputData = useMemo(
    () => [
      { fieldName: "franchiseId", title: "Franchise ID", placeholder: "Enter franchise ID", disabled: true },
      { fieldName: "franchiseName", title: "Franchise Name", placeholder: "Enter franchise name" },
      { fieldName: "country", title: "Country", placeholder: "Tap to select", type: "select" },
      { fieldName: "locationAddress", title: "Location/Address", placeholder: "Enter location/address" },
      { fieldName: "contactPerson", title: "Contact Person", placeholder: "Enter contact person" },
      { fieldName: "franchiseType", title: "Franchise Type", placeholder: "Tap to select" },
    ],
    []
  );

  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] = useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) setFinalJson(formDataBulkUpload);
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const queryParams = new URLSearchParams({ limit: "1000", page: "1" }).toString();
        const res = await get(`/master/franchises?${queryParams}`);
        setMasterFranchises(res?.data?.data || []);
      } catch (e) {
        setMasterFranchises([]);
      } finally {
        setLoadingMasters(false);
      }
    };
    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable]);

  const franchiseIdOptions = useMemo(() => {
    const ids = (masterFranchises || [])
      .map((f) => f?.franchiseId)
      .filter((v) => typeof v === "string" && v.trim().length > 0);
    return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
  }, [masterFranchises]);

  const getSelectedFranchiseIdForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    const selected = row?.[EXCEL_FRANCHISE_ID_KEY] || row?.franchiseId || row?.franchiseID || "";
    return String(selected || "");
  };

  const setSelectedFranchiseIdForIndex = (index, nextValue) => {
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      arr[index] = { ...existing, [EXCEL_FRANCHISE_ID_KEY]: nextValue };
      return arr;
    });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      const selected = getSelectedFranchiseIdForIndex(idx);
      return !selected || selected === "add_new";
    });
  }, [matchData, formDataBulkUpload]);

  const openAddMasterDrawer = (index) => {
    const entered = String(getEnteredFranchiseId(matchData?.[index]) || "").trim();
    setAddRowIndex(index);
    setPendingCreatedFranchiseId(entered);
    setAddMasterElement({
      franchiseName: "",
      franchiseId: entered,
      country: "",
      locationAddress: "",
      contactPerson: "",
      franchiseType: "",
    });
    setAddMasterOpen(true);
    setExpandedRows((prev) => ({ ...prev, [index]: true }));
  };

  const refreshMasterFranchisesAndMarkMatched = async () => {
    try {
      const queryParams = new URLSearchParams({ limit: "1000", page: "1" }).toString();
      const res = await get(`/master/franchises?${queryParams}`);
      const list = res?.data?.data || [];
      setMasterFranchises(list);
      if (typeof addRowIndex === "number" && pendingCreatedFranchiseId) {
        setSelectedFranchiseIdForIndex(addRowIndex, pendingCreatedFranchiseId);
        setMatchData((prev) => (prev || []).map((r, i) => (i === addRowIndex ? setMatchedFlag(r, true) : r)));
      }
    } finally {
      setExpandedRows((prev) => ({ ...prev, ...(addRowIndex !== null ? { [addRowIndex]: false } : {}) }));
      setAddRowIndex(null);
      setPendingCreatedFranchiseId("");
      setAddMasterElement(null);
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) => (Array.isArray(prev) ? prev.map((entry) => ({ ...entry, "Document (optional)": null })) : prev));
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) => (Array.isArray(prev) ? prev.map((entry) => ({ ...entry, "Document (optional)": files })) : prev));
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry) => ({ ...entry, "Document (optional)": files })));
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) => (Array.isArray(prev) ? prev.map((entry) => ({ ...entry, "Document (optional)": null })) : prev));
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry) => ({ ...entry, "Document (optional)": null })));
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) => (Array.isArray(prev) ? prev.map((entry, i) => (i === index ? { ...entry, "Document (optional)": files } : entry)) : prev));
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry, i) => (i === index ? { ...entry, "Document (optional)": files } : entry)));
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) => (Array.isArray(prev) ? prev.map((entry, i) => (i === index ? { ...entry, "Document (optional)": null } : entry)) : prev));
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry, i) => (i === index ? { ...entry, "Document (optional)": null } : entry)));
    }
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push("Please select a document upload type when document upload is confirmed.");
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUse = Array.isArray(finalJson) && finalJson.length > 0 ? finalJson : formDataBulkUpload;
      const uploadData = { excelData: dataToUse, documentUploadType, commonDocument, individualDocuments };
      const res = await post(bulkUploadUrl, uploadData);
      setsnackOpen?.({ open: true, message: res?.data?.message || "Uploaded successfully", severity: "success" });
      onConfirm?.();
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details?.map((el) => el?.error || el?.reason)?.join(", ");
      setsnackOpen?.({ open: true, message: errMessage || e?.response?.data?.message || "Upload failed", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc = documentUploadType === "same" && commonDocument && commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{ "& .MuiDialog-paper": { width: "85%", margin: "16px", maxHeight: "calc(100% - 32px)", zIndex: 1000 } }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review unmatched franchise IDs and map them with master franchise IDs.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Data Entered</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Master Data</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Action</TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Document</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Franchise ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Franchise ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} />
                  {documentUploadConfirmed && documentUploadType === "individual" && <TableCell sx={{ fontWeight: "bold" }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const entered = getEnteredFranchiseId(row);
                  const matched = getMatchedFlag(row);
                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{entered || "-"}</TableCell>
                        <TableCell>
                          {matched ? (
                            <Chip label="Matched" color="success" size="small" />
                          ) : (
                            <FormControl size="small" sx={{ minWidth: "14rem" }}>
                              <Select
                                value={getSelectedFranchiseIdForIndex(index) || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  if (nextValue === "add_new") {
                                    setSelectedFranchiseIdForIndex(index, "add_new");
                                    openAddMasterDrawer(index);
                                    return;
                                  }
                                  setSelectedFranchiseIdForIndex(index, nextValue);
                                  setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                  setAddMasterOpen(false);
                                  setMatchData((prev) => (prev || []).map((r, i) => (i === index ? setMatchedFlag(r, Boolean(nextValue)) : r)));
                                }}
                                fullWidth
                                displayEmpty
                                disabled={loadingMasters}
                              >
                                <MenuItem value="">Tap to Select</MenuItem>
                                <MenuItem value="add_new">Add New Franchise +</MenuItem>
                                {franchiseIdOptions.map((id) => (
                                  <MenuItem key={id} value={id}>
                                    {id}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {!matched &&
                            (expandedRows[index] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                  setAddMasterOpen(false);
                                }}
                                sx={{ minWidth: "80px" }}
                              >
                                Close
                              </Button>
                            ) : (
                              <Chip
                                label={getSelectedFranchiseIdForIndex(index) ? "Selected" : "Unmatched"}
                                color={getSelectedFranchiseIdForIndex(index) ? "info" : "warning"}
                                size="small"
                              />
                            ))}
                        </TableCell>
                        {documentUploadConfirmed && documentUploadType === "individual" && (
                          <TableCell>
                            <BulkFileUpload
                              handleChange={handleIndividualDocumentChange}
                              handleClear={handleIndividualDocumentClear}
                              filesList={
                                individualDocuments[index] ||
                                formDataBulkUpload?.[index]?.["Document (optional)"] ||
                                finalJson?.[index]?.["Document (optional)"] ||
                                []
                              }
                              index={index}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={documentUploadConfirmed && documentUploadType === "individual" ? 4 : 3}>
                          <Collapse in={Boolean(expandedRows[index]) && addRowIndex === index} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2 }}>
                              <AddFranchise
                                id=""
                                addData={addMasterOpen && addRowIndex === index}
                                setAddData={(next) => {
                                  setAddMasterOpen(next);
                                  if (!next) setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                }}
                                data={masterFranchises}
                                element={addMasterElement}
                                addUrl="/master/franchises"
                                updateUrl="/master/franchises"
                                fetchData={refreshMasterFranchisesAndMarkMatched}
                                setsnackOpen={setsnackOpen}
                                formInputData={masterFormInputData}
                                embedded
                                embeddedTitle="Add Franchise"
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Do you want to upload document?</FormLabel>
                <RadioGroup value={documentUploadConfirmed ? "yes" : "no"} onChange={(e) => handleDocumentUploadConfirmation(e.target.value === "yes")} row>
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
              {documentUploadConfirmed && (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Document Upload Type</FormLabel>
                  <RadioGroup value={documentUploadType} onChange={handleDocumentUploadTypeChange} row>
                    <FormControlLabel value="same" control={<Radio />} label="Same File for All Records" />
                    <FormControlLabel value="individual" control={<Radio />} label="Individual File for Each Record" />
                  </RadioGroup>
                </FormControl>
              )}
              {documentUploadConfirmed && documentUploadType === "same" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload Common Document:
                </Typography>
                <BulkFileUpload handleChange={handleCommonDocumentChange} handleClear={handleCommonDocumentClear} filesList={commonDocument} />
              </Box>
              )}
            </Stack>
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
              <Typography variant="h6" color="error" sx={{ mb: 1, fontWeight: "bold" }}>
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  • {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={onClose} variant="outlined" color="primary" sx={{ marginRight: "1rem" }} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmUpload} variant="contained" color="primary" disabled={disableConfirm || uploading}>
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showNoDocumentDialog} onClose={() => setShowNoDocumentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">No Documents Uploaded</Typography>
            <IconButton onClick={() => setShowNoDocumentDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven't uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can still upload documents later if needed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={() => setShowNoDocumentDialog(false)} variant="outlined" color="primary" sx={{ marginRight: "1rem" }}>
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;
